import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge, { statusColorMap } from "@/components/StatusBadge";
import { LostItem } from "@/types";
import { Signature, Undo, Edit, Trash, Info, Clock, PackageX } from "lucide-react";
import { useState, useEffect } from "react";
import PhotoUploadModal from "./PhotoUploadModal";
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from "date-fns";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

interface LostFoundGridCardProps {
  item: LostItem;
  onDeliver: (item: LostItem) => void;
  onUndo: (item: LostItem) => void;
  onEdit: (item: LostItem) => void;
  onDelete: (item: LostItem) => void;
  onDiscard: (item: LostItem) => void;
  openTimeline: (item: LostItem) => void;
  openInfo: (item: LostItem) => void;
  setSelectedItem: (item: LostItem) => void;
  setDeleteDialogOpen: (open: boolean) => void;
}

const LostFoundGridCard = ({
  item,
  onDeliver,
  onUndo,
  onEdit,
  onDelete,
  onDiscard,
  openTimeline,
  openInfo,
  setSelectedItem,
  setDeleteDialogOpen
}: LostFoundGridCardProps) => {
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [signedPhotoUrl, setSignedPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    async function fetchSignedUrl() {
      if (item.photo && !item.photo.startsWith('data:')) {
        const { data, error } = await supabase.storage.from('lost-and-found-photos').createSignedUrl(item.photo, 60 * 60);
        if (isMounted && data?.signedUrl) setSignedPhotoUrl(data.signedUrl);
      } else {
        setSignedPhotoUrl(null);
      }
    }
    fetchSignedUrl();
    return () => { isMounted = false; };
  }, [item.photo]);

  // Determine disabled state for actions
  const isDeleted = item.status === "Deleted";
  const isDiscarded = item.status === "Discarded";
  const isDelivered = item.status === "Delivered";
  const disableActions = isDeleted || isDiscarded || isDelivered;

  // Determine which action is enabled for non-pending statuses
  let enableEdit = false, enableDelete = false, enableArchive = false;
  if (item.status === "Pending") {
    enableEdit = enableDelete = enableArchive = true;
  } else if (item.status === "Deleted") {
    enableDelete = true;
  } else if (item.status === "Discarded") {
    enableArchive = true;
  }

  return (
    <Card
      className={
        `relative p-3 flex flex-col justify-between min-h-[320px] bg-white transition-shadow duration-150 ` +
        `hover:shadow-lg hover:ring-2 hover:ring-primary/20 border-l-4`
      }
      style={{ borderLeftColor: statusColorMap[item.status] }}
    >
      {/* Subtle Lost Item ID in top-left */}
      <div className="absolute left-3 top-3 text-xs text-muted-foreground font-mono opacity-70 select-none">
        #{String(item.sequential_number).padStart(3, '0')}
      </div>
      {/* Info button in top-right */}
      <div className="absolute right-3 top-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => openInfo(item)} className="h-7 w-7 text-blue-500 hover:text-blue-600">
                <Info size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Item Info</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <CardContent className="flex-1 flex flex-col justify-between pt-8 pb-2">
        {/* Photo */}
        <div className="mb-2">
          {item.photo && (item.photo.startsWith('data:') || signedPhotoUrl) ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-full h-32 p-0 mb-2"
                    onClick={() => setPhotoModalOpen(true)}
                    disabled={disableActions}
                  >
                    <img
                      src={item.photo.startsWith('data:') ? item.photo : signedPhotoUrl || ''}
                      alt={item.description}
                      className="w-full h-32 object-cover rounded-md"
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Preview / Change photo</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-32 mb-2"
                    onClick={() => setPhotoModalOpen(true)}
                    disabled={disableActions}
                  >
                    Upload Photo
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Upload photo</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {/* Status, Entry Date, Unit # on one row */}
        <div className="flex flex-row items-center gap-2 text-xs text-muted-foreground mb-2">
          <StatusBadge status={item.status} />
          <span className="mx-1">•</span>
          <span>{item.room_number || 'N/A'}</span>
          <span className="mx-1">•</span>
          <span>{item.date ? formatDistanceToNow(new Date(item.date), { addSuffix: true }) : ''}</span>
        </div>
        {/* Title (description) */}
        <div className={isDeleted ? "text-sm line-clamp-2 line-through text-gray-400 font-semibold mb-1" : "text-sm line-clamp-2 font-semibold mb-1"}>{item.description}</div>
        {/* Location */}
        <div className={isDeleted ? "mt-1 text-xs text-gray-400 line-through" : "mt-1 text-xs text-muted-foreground"}>
          Location: {(item as any).location || "N/A"}
        </div>
        {/* Reported by (if available) */}
        {item.found_by && (
          <div className="mt-1 text-xs text-muted-foreground italic">Reported by: {item.found_by}</div>
        )}
        {/* Actions */}
        <div className="mt-3 space-y-2">
          {(isDeleted || isDiscarded || isDelivered) ? (
            <Button
              size="sm"
              className="w-full mb-1"
              variant="outline"
              onClick={() => {
                if (item && item.id) onUndo(item);
              }}
            >
              <Undo size={16} className="mr-1" /> Undo Changes
            </Button>
          ) : (
            <Button size="sm" className="w-full mb-1" onClick={() => {
              if (item && item.id) onDeliver(item);
            }}>
              Mark as Delivered
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">Actions</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      onSelect={() => onEdit(item)}
                      className="flex items-center gap-2"
                      disabled={!enableEdit}
                    >
                      <Edit size={16} className={!enableEdit ? 'opacity-40' : 'text-primary group-hover:opacity-100'} />
                      Edit
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      onSelect={() => onDelete(item)}
                      className={enableDelete ? 'text-red-500' : 'opacity-40'}
                      disabled={!enableDelete}
                    >
                      <Trash size={16} />
                      Delete
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent>Delete</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      onSelect={() => onDiscard(item)}
                      className={enableArchive ? 'text-orange-500' : 'opacity-40'}
                      disabled={!enableArchive}
                    >
                      <PackageX size={16} />
                      Discard
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent>Discard</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
      <PhotoUploadModal
        open={photoModalOpen}
        imageUrl={item.photo}
        onClose={() => setPhotoModalOpen(false)}
        onSave={() => {}}
        itemId={String(item.id)}
      />
    </Card>
  );
};

export default LostFoundGridCard;
